import { TaskResponse, TaskUiElementsResponse } from "@9amhealth/openapi";
import { Blac, Cubit } from "blac-next";
import { KnownProgram } from "src/state/ProgramBloc/ProgramBloc";
import TaskManagementBloc, {
  TaskKey
} from "src/state/TaskManagementBloc/TaskManagementBloc";
import type { TaskResponseKnown } from "src/state/TaskManagementBloc/TaskManagementBloc";
import { createTimestamps } from "./debugTaskUtils";

const STORAGE_KEY = "debug-initial-labs-mocks";

export interface TaskConfig {
  slug: string;
  label: string;
  defaultGroup: string;
}

export const TASK_CONFIGS: TaskConfig[] = [
  {
    slug: TaskKey.INITIAL_LAB_QUESTIONNAIRE,
    label: "Lab Questionnaire",
    defaultGroup: "initial-lab-order"
  },
  {
    slug: TaskKey.MEDICAL_INSURANCE,
    label: "Medical Insurance",
    defaultGroup: "onboarding"
  },
  {
    slug: TaskKey.COMPLETE_INITIAL_LAB_ORDER,
    label: "Lab Order",
    defaultGroup: "initial-lab-order"
  }
];

export interface LabOrderAdditionalData {
  labOrderProvider: string;
  requisitionFileId: string;
  appointmentUrl: string;
  appointmentId: string;
  labOrderPscLocationName: string;
}

export interface MedicalInsuranceAdditionalData {
  photoUploadCanBeSkippedByUser: boolean;
}

export interface MockTaskState {
  enabled: boolean;
  status: TaskResponse.status;
  title: string;
  iconType: TaskUiElementsResponse.iconType;
  popupType: string;
  userCanSee: boolean;
  userCanSkip: boolean;
  adminCanSkip: boolean;
  adminCanComplete: boolean;
  labOrder: LabOrderAdditionalData;
  medicalInsurance: MedicalInsuranceAdditionalData;
}

export const DEFAULT_MOCK_STATE: MockTaskState = {
  enabled: false,
  status: TaskResponse.status.AVAILABLE,
  title: "",
  iconType: TaskUiElementsResponse.iconType.FORM,
  popupType: "undefined",
  userCanSee: true,
  userCanSkip: false,
  adminCanSkip: false,
  adminCanComplete: false,
  labOrder: {
    labOrderProvider: "",
    requisitionFileId: "",
    appointmentUrl: "",
    appointmentId: "",
    labOrderPscLocationName: ""
  },
  medicalInsurance: {
    photoUploadCanBeSkippedByUser: false
  }
};

export const LAB_ORDER_PROVIDERS = [
  "",
  "LABCORP",
  "QUEST",
  "GETLABS",
  "TASSO",
  "KWIK_TRIP_CENTER_FOR_HEALTH",
  "OSF_HEALTH_CARE"
] as const;

const getSlugAdditionalData = (
  slug: string,
  mock: MockTaskState
): Record<string, unknown> => {
  if (slug === TaskKey.COMPLETE_INITIAL_LAB_ORDER) {
    const d = mock.labOrder;
    return {
      ...(d.labOrderProvider && {
        labOrderProvider: d.labOrderProvider,
        provider: d.labOrderProvider
      }),
      ...(d.requisitionFileId && { requisitionFileId: d.requisitionFileId }),
      ...(d.appointmentUrl && { appointmentUrl: d.appointmentUrl }),
      ...(d.appointmentId && { appointmentId: d.appointmentId }),
      ...(d.labOrderPscLocationName && {
        labOrderPscLocationName: d.labOrderPscLocationName
      })
    };
  }
  if (slug === TaskKey.MEDICAL_INSURANCE) {
    return {
      photoUploadCanBeSkippedByUser:
        mock.medicalInsurance.photoUploadCanBeSkippedByUser
    };
  }
  return {};
};

const buildMockTask = (
  config: TaskConfig,
  mock: MockTaskState
): TaskResponseKnown => ({
  id: `debug-${config.slug}`,
  slug: config.slug,
  program: KnownProgram.ONBOARDING,
  group: config.defaultGroup,
  status: mock.status,
  ...createTimestamps(),
  additionalData: {
    skippable: mock.userCanSkip,
    canBeSkippedByUser: mock.userCanSkip,
    canBeSkippedByCareTeam: mock.adminCanSkip,
    canBeCompletedByCareTeam: mock.adminCanComplete,
    popupType: mock.popupType,
    title: mock.title,
    iconType: mock.iconType,
    ...getSlugAdditionalData(config.slug, mock)
  },
  uiElements: {
    title: mock.title,
    iconType: mock.iconType,
    popupType: mock.popupType
  },
  behavior: {
    userCanSee: mock.userCanSee,
    userCanSkip: mock.userCanSkip,
    adminCanSkip: mock.adminCanSkip,
    adminCanComplete: mock.adminCanComplete
  }
});

export const stateFromTask = (task: TaskResponseKnown): MockTaskState => ({
  enabled: true,
  status: task.status as TaskResponse.status,
  title: (task.uiElements as TaskUiElementsResponse)?.title ?? "",
  iconType:
    (task.uiElements as TaskUiElementsResponse)?.iconType ??
    TaskUiElementsResponse.iconType.FORM,
  popupType:
    (task.uiElements as TaskUiElementsResponse)?.popupType ?? "undefined",
  userCanSee: task.behavior?.userCanSee ?? true,
  userCanSkip: task.behavior?.userCanSkip ?? false,
  adminCanSkip: task.behavior?.adminCanSkip ?? false,
  adminCanComplete: task.behavior?.adminCanComplete ?? false,
  labOrder: {
    labOrderProvider: String(
      task.additionalData?.labOrderProvider ??
        task.additionalData?.provider ??
        ""
    ),
    requisitionFileId: String(task.additionalData?.requisitionFileId ?? ""),
    appointmentUrl: String(task.additionalData?.appointmentUrl ?? ""),
    appointmentId: String(task.additionalData?.appointmentId ?? ""),
    labOrderPscLocationName: String(
      task.additionalData?.labOrderPscLocationName ?? ""
    )
  },
  medicalInsurance: {
    photoUploadCanBeSkippedByUser: Boolean(
      task.additionalData?.photoUploadCanBeSkippedByUser
    )
  }
});

interface DebugInitialLabsState {
  mockStates: Record<string, MockTaskState>;
}

const loadPersistedStates = (): Record<string, MockTaskState> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, MockTaskState>;
      const result: Record<string, MockTaskState> = {};
      for (const config of TASK_CONFIGS) {
        result[config.slug] = parsed[config.slug]
          ? { ...DEFAULT_MOCK_STATE, ...parsed[config.slug] }
          : { ...DEFAULT_MOCK_STATE };
      }
      return result;
    }
  } catch {
    // ignore corrupt data
  }
  return Object.fromEntries(
    TASK_CONFIGS.map((c) => [c.slug, { ...DEFAULT_MOCK_STATE }])
  );
};

export default class DebugInitialLabsCubit extends Cubit<DebugInitialLabsState> {
  constructor() {
    super({
      mockStates: loadPersistedStates()
    });
  }

  private persistStates = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state.mockStates));
  };

  private applyStates = () => {
    const taskBloc = Blac.getBloc(TaskManagementBloc);
    const states = this.state.mockStates;
    const enabledConfigs = TASK_CONFIGS.filter((c) => states[c.slug].enabled);

    if (enabledConfigs.length === 0) {
      taskBloc.setMockProgramResponsesEnabled(false);
      void taskBloc.loadProgramTasks(KnownProgram.ONBOARDING);
      return;
    }

    const existingTasks = taskBloc.state.tasks.filter(
      (t) =>
        t.program === KnownProgram.ONBOARDING &&
        !enabledConfigs.some((c) => c.slug === t.slug)
    );

    const mockTasks = enabledConfigs.map((c) =>
      buildMockTask(c, states[c.slug])
    );

    taskBloc.setMockProgramResponsesEnabled(true);
    taskBloc.setMockResponseForProgram(
      KnownProgram.ONBOARDING,
      JSON.stringify([...existingTasks, ...mockTasks])
    );
    void taskBloc.loadProgramTasks(KnownProgram.ONBOARDING);
  };

  init = () => {
    const hasEnabled = TASK_CONFIGS.some(
      (c) => this.state.mockStates[c.slug].enabled
    );
    if (hasEnabled) {
      this.applyStates();
    }
  };

  cleanup = () => {
    const taskBloc = Blac.getBloc(TaskManagementBloc);
    taskBloc.setMockProgramResponsesEnabled(false);
    void taskBloc.loadProgramTasks(KnownProgram.ONBOARDING);
  };

  updateMockState = (slug: string, state: MockTaskState) => {
    this.emit({
      mockStates: { ...this.state.mockStates, [slug]: state }
    });
    this.persistStates();
    this.applyStates();
  };
}
